import {
  DateRangeInterval,
  DateRangeIntervalWithoutWeek,
  DateRangeObject,
  IssueChartFilters,
} from 'shared/types/analytics';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { AnyFilters, ChartFilter } from 'shared/types/analytics';
import { FilterTypes } from 'shared/types/analytics';
import { shortISOStringToProjectTimezonedDate } from 'shared/utils/date/dates';
import { DayMomentValidation } from 'shared/types/time';

/** Any chart's filters: the setters only need the filters of the data scope. */
type FiltersWithDataScope = Pick<AnyFilters<unknown>, 'dataScope'>;

export function setMultiselectFilterValue<F extends FiltersWithDataScope>(
  prevFilters: F,
  filterIndex: number,
  value: string[]
): F {
  return setMultiselect(prevFilters, filterIndex, value, 'value');
}

/** Sets the date range: the first filter of the data scope. */
export function setDateRangeFilterValue(
  prevFilters: IssueChartFilters,
  value: DateRangeObject
): IssueChartFilters {
  const [dateRangeFilter, ...otherFilters] = prevFilters.dataScope.filters;
  return {
    ...prevFilters,
    dataScope: {
      ...prevFilters.dataScope,
      filters: [{ ...dateRangeFilter, value }, ...otherFilters],
    },
  };
}

function setMultiselect<F extends FiltersWithDataScope>(
  prevFilters: F,
  filterIndex: number,
  value: string[] | LabelledEntity[],
  key: string
): F {
  if (!prevFilters) return prevFilters;
  const result = {
    ...prevFilters,
    dataScope: {
      ...prevFilters.dataScope,
      filters: [...prevFilters.dataScope.filters],
    },
  };
  result.dataScope.filters[filterIndex] = {
    ...prevFilters.dataScope.filters[filterIndex],
    [key]: [...value],
  };
  return result;
}

export function createLabels(array: LabelledEntity[]): HashMap<string> {
  return array.reduce((labels: HashMap<string>, element) => {
    labels[element._id] = element.label;
    return labels;
  }, {});
}

export function clearFilterByFieldVisibility<
  T extends { dataScope: { filters: ChartFilter<any>[] } }
>(visibleFields: Set<string>, filters: T | undefined): T | undefined {
  if (!filters) return filters;
  return {
    ...filters,
    dataScope: {
      ...filters.dataScope,
      filters: filters.dataScope.filters.map((filter: ChartFilter<any>) => {
        if (!filter.fieldRelation || visibleFields.has(filter.fieldRelation)) {
          return filter;
        }
        return clearedByType(filter);
      }),
    },
  };
}

function clearedByType(filter: ChartFilter<any>): ChartFilter<any> {
  switch (filter.type) {
    case FilterTypes.multiselect: {
      return { ...filter, value: [] };
    }
    case FilterTypes.checkbox: {
      return { ...filter, value: {} };
    }
    case FilterTypes.select:
    case FilterTypes.radio: {
      return { ...filter, value: null };
    }
    default: {
      return filter;
    }
  }
}

export function isDateDifferenceInvalid(
  dateFrom: string,
  dateTo: string,
  interval: DateRangeInterval | DateRangeIntervalWithoutWeek,
  timezone: string
): boolean {
  const intervalString = toDurationObjectUnits(interval);
  const startL = shortISOStringToProjectTimezonedDate(
    dateFrom,
    timezone,
    DayMomentValidation.start
  );
  const endL = shortISOStringToProjectTimezonedDate(
    dateTo,
    timezone,
    DayMomentValidation.end
  );
  const difference = endL.diff(startL, intervalString);

  return difference[intervalString] > 366;
}

function toDurationObjectUnits(
  dateRange: DateRangeInterval | DateRangeIntervalWithoutWeek
): 'days' | 'months' | 'years' | 'weeks' {
  return (dateRange + 's') as 'days' | 'months' | 'years' | 'weeks';
}
